import React, { useCallback, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import Button from "./Button";
import { Cancel, Close, TipsAndUpdates } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import {
    MainErrorInfo,
    SingleAlertInfo,
    editAppConfiguration,
    fetchAppConfigList,
    setMainErrorObj,
    setSingleAlertObj,
} from "../../redux/slice/commonSlice";
import { useAppDispatch } from "../../redux/slice/redux-hooks";
import { Box, Grid, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useModalStyles } from "./useModalStyles";

interface AppConfigProps {
    open: boolean;
    onClose: () => void;
    appConfigData: any;
    originalConfigValue: any;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="down" ref={ref} {...props} />;
});

const UpdateAppConfigPopup: React.FC<AppConfigProps> = ({
    open,
    onClose,
    appConfigData,
    originalConfigValue
}) => {
    const {  handleSubmit } = useForm({});
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isDark = theme.palette.mode === "dark";
    const { tokens, paperSx, headerSx, iconBoxSx, closeIconSx, bodySx, footerSx, primaryBtnSx, cancelBtnSx } = useModalStyles();


    const onSubmit = async () => {
        if (appConfigData?.appconfigId && appConfigData?.configValue) {
            const data = {
                appconfigId: appConfigData?.appconfigId,
                configValue: (appConfigData?.configValue).trim()
            }
            const response = await dispatch(editAppConfiguration(data))
            if (response.payload && response.payload.code === "V3OK") {
                let successObj: SingleAlertInfo = {
                  message:response.payload.message,
                  alertType: "success",
                };
              dispatch(setSingleAlertObj(successObj));

            }
               
           await dispatch(fetchAppConfigList())
        }
        onClose();
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <>
            <Dialog
                open={open}
                TransitionComponent={Transition}
                keepMounted
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{ sx: paperSx(isMobile) }}
                aria-describedby="alert-dialog-slide-description"
                onClick={(event) => {
                    event.stopPropagation();
                }}
            >
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box sx={headerSx}>
                        <Box sx={iconBoxSx(tokens.accent)}>
                            <TipsAndUpdates sx={{ fontSize: "1.25rem", color: tokens.accent }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                fontWeight={700}
                                sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}
                            >
                                Update Configuration
                            </Typography>
                            <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                                Please review before applying this configuration change
                            </Typography>
                        </Box>
                        <IconButton onClick={handleClose} size="small" sx={closeIconSx}>
                            <Close sx={{ fontSize: "1rem" }} />
                        </IconButton>
                    </Box>
                    <Box sx={{ ...bodySx, py: 2.8 }}>
                        <Box
                            sx={{
                                border: `1px solid ${tokens.border}`,
                                borderRadius: "12px",
                                backgroundColor: tokens.inputBg,
                                px: 2,
                                py: 1.6,
                            }}
                        >
                            <Box
                                sx={{
                                    maxHeight: "460px",
                                    overflowY: "auto",
                                    p: 2.5,
                                    borderRadius: "10px",
                                    backgroundColor: isDark ? "#1e2533" : "#f8fafc",
                                    border: `1px solid ${isDark ? "#334155" : "#e2e8f0"}`,
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                    fontSize: "0.85rem",
                                    lineHeight: 1.6,
                                    scrollbarWidth: "thin",
                                    scrollbarColor: isDark ? "#475569 #1e2533" : "#94a3b8 #f1f5f9",
                                    "&::-webkit-scrollbar": { width: "7px" },
                                    "&::-webkit-scrollbar-thumb": {
                                        backgroundColor: isDark ? "#475569" : "#94a3b8",
                                        borderRadius: "20px",
                                    },
                                    "&::-webkit-scrollbar-thumb:hover": {
                                        backgroundColor: isDark ? "#64748b" : "#475569",
                                    },
                                }}
                            >
                                <Typography
                                    component="div"
                                    sx={{
                                        fontSize: "0.95rem",
                                        lineHeight: 1.75,
                                        color: tokens.textPrimary,
                                        textAlign: "center",
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        overflowWrap: "anywhere",
                                    }}
                                >
                                    <span>The configuration value is currently "</span><span style={{ color: "red", fontSize: "15px", fontWeight: "bold" }}>{originalConfigValue}</span><span>" and will be updated to "<span style={{ color: "#66bb6a", fontSize: "15px", fontWeight: "bold" }}>{appConfigData.configValue}</span>" shortly.</span>
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={footerSx}>
                        <Grid container justifyContent="flex-end" spacing={2}>
                            <Grid item xs={12} sm="auto">
                                <Button type="submit" startIcon={<TipsAndUpdates />} fullWidth sx={primaryBtnSx}>
                                    Update
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm="auto">
                                <Button color="error" variant="outlined" startIcon={<Cancel />} onClick={() => handleClose()} fullWidth sx={cancelBtnSx}>
                                    Cancel
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </form>
            </Dialog>
        </>
    );
};

export default UpdateAppConfigPopup;
